import { type ReactNode } from "react";
import { cn } from "@strivelabs/ui-core";
import { FileText, Loader2, Zap, X } from "lucide-react";
import type { ComposerAttachmentItem } from "./types";
import { COMPOSER_MODE_ACCENT } from "./tones";

/** Context row above the input holding the skill chip and attachment chips. */
export const ComposerChips = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("flex flex-wrap gap-2 px-3 pt-3", className)}>
    {children}
  </div>
);

export interface ComposerSkillChipProps {
  label: string;
  onRemove?: () => void;
}

/** The active-skill chip. */
export const ComposerSkillChip = ({ label, onRemove }: ComposerSkillChipProps) => (
  <div className="inline-flex items-center gap-1.5 rounded-lg border bg-muted py-1 pl-2.5 pr-1.5 text-foreground duration-150 animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none">
    <Zap className="size-3" style={{ color: COMPOSER_MODE_ACCENT }} />
    <span className="text-xs font-medium">{label}</span>
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove skill"
        className="flex size-4 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="size-2.5" strokeWidth={2.6} />
      </button>
    )}
  </div>
);

export interface ComposerAttachmentProps {
  item: ComposerAttachmentItem;
  onRemove?: (id: string) => void;
  onOpen?: (id: string) => void;
}

/** A single attachment chip. */
export const ComposerAttachment = ({
  item,
  onRemove,
  onOpen,
}: ComposerAttachmentProps) => (
  <div
    className={cn(
      "flex items-center gap-2 rounded-md border bg-muted/40 py-1 pl-1 pr-2 transition-colors duration-150 animate-in fade-in-0 zoom-in-95 hover:bg-muted motion-reduce:animate-none",
      item.status === "error" && "border-destructive/40 bg-destructive/5",
    )}
  >
    <button
      type="button"
      onClick={() => onOpen?.(item.id)}
      className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded bg-muted"
    >
      {item.kind === "image" && item.thumbUrl ? (
        <img src={item.thumbUrl} alt="" className="size-full object-cover" />
      ) : (
        <FileText className="size-[15px] text-muted-foreground" />
      )}
      {item.status === "uploading" && (
        <span className="absolute inset-0 flex items-center justify-center bg-background/60">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </span>
      )}
    </button>
    <div className="min-w-0 max-w-[150px]">
      <div className="truncate text-xs font-medium">{item.name}</div>
      {item.status === "error" ? (
        <div
          title={item.error ?? "Upload failed"}
          className={cn(
            "truncate text-[10px] text-destructive",
            !item.error && "uppercase tracking-wide",
          )}
        >
          {item.error ?? "Upload failed"}
        </div>
      ) : (
        item.meta && (
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {item.meta}
          </div>
        )
      )}
    </div>
    {onRemove && (
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        aria-label="Remove attachment"
        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="size-3" strokeWidth={2.4} />
      </button>
    )}
  </div>
);

export interface ComposerAttachmentsProps {
  items: ComposerAttachmentItem[];
  onRemove?: (id: string) => void;
  onOpen?: (id: string) => void;
}

/** Renders the attachment chips for the given items. */
export const ComposerAttachments = ({
  items,
  onRemove,
  onOpen,
}: ComposerAttachmentsProps) => (
  <>
    {items.map((item) => (
      <ComposerAttachment
        key={item.id}
        item={item}
        onRemove={onRemove}
        onOpen={onOpen}
      />
    ))}
  </>
);
